/**
 * Logged-out "remember me" hint for the marketing site chrome.
 *
 * A display-only hint so the logged-out header can show "Continue as …"
 * affordances. It persists in localStorage until the visitor explicitly
 * clears it — there is deliberately NO EXPIRY and NO session data here:
 * only `{ email, initial, ts }`. Never avatar URLs, tokens, or user ids.
 */

export interface RememberHint {
  email: string;
  /** Single uppercase display letter derived from the email. */
  initial: string;
  /** Epoch millis when the hint was written (bookkeeping only — not an expiry). */
  ts: number;
}

const STORAGE_KEY = "sylviq.remember_hint";

function isHint(value: unknown): value is RememberHint {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.email === "string" &&
    candidate.email.trim() !== "" &&
    typeof candidate.initial === "string" &&
    candidate.initial.trim() !== "" &&
    typeof candidate.ts === "number" &&
    Number.isFinite(candidate.ts)
  );
}

function initialFor(email: string): string {
  const match = email.match(/[A-Za-z0-9]/);
  return (match?.[0] ?? "S").toUpperCase();
}

/**
 * Reads the stored hint, or `null` when there is none, it is corrupt, or
 * storage is unavailable (e.g. private browsing). Stored hints never expire —
 * only {@link clearHint} removes one.
 */
export function readHint(): RememberHint | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isHint(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Stores a hint for `email`, returning it — or `null` when the email is
 * blank or storage is unavailable (e.g. private browsing).
 */
export function writeHint(email: string): RememberHint | null {
  const normalized = email.trim();
  if (normalized === "") return null;
  const hint: RememberHint = {
    email: normalized,
    initial: initialFor(normalized),
    ts: Date.now(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(hint));
    return hint;
  } catch {
    return null;
  }
}

/** Removes the stored hint. Never throws, even when storage is unavailable. */
export function clearHint(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private browsing (or no storage at all): nothing persisted, nothing to clear.
  }
}
